import { useMutation } from '@tanstack/react-query';
import { authVerifyEmailCreate } from '@wellstone-solutions/api/v2';
import { isAxiosError } from 'axios';

import { useAuthStore } from '@modules/auth/store/auth-store';

import type {
  VerifyEmailToken,
  VerifyEmailTokenInputRequest,
} from '@wellstone-solutions/api/v2';
import type { AxiosError, AxiosResponse } from 'axios';

const useVerifyEmail = () => {
  const {
    setUserInfo,
    setReadyToLogin,
    setVerificationError,
    clearVerificationError,
  } = useAuthStore();

  return useMutation<
    AxiosResponse<VerifyEmailToken>,
    AxiosError<{ [key: string]: string[] }>,
    VerifyEmailTokenInputRequest
  >({
    mutationFn: async (data) => {
      try {
        // Clear any previous errors when starting verification
        clearVerificationError();

        const response = await authVerifyEmailCreate(data);

        // If there's an email in the success response, update the store
        if (response?.data?.email) {
          setUserInfo({ email: response.data.email });
        }
        setReadyToLogin();

        return response;
      } catch (error) {
        // Log the error
        console.error('Verify email API error:', error);

        if (!isAxiosError<VerifyEmailToken>(error)) {
          throw error;
        }

        const { email, message } = error.response?.data ?? {};
        const errorMessage = Array.isArray(message) ? message[0] : undefined;

        if (email) setUserInfo({ email });

        if (!errorMessage) {
          // Generic error message
          setVerificationError(
            'Unable to verify your email. Please try again later.'
          );

          throw error;
        }

        // handle token expired error
        if (errorMessage === 'Token expired') {
          setVerificationError(
            'Your verification link has expired. Please request a new one.'
          );
        } else {
          setVerificationError(errorMessage);
        }

        throw error;
      }
    },
  });
};

export { useVerifyEmail };
